import React from 'react';
import { 
    Modal, 
    Box, 
    Typography, 
    IconButton, 
    Grid, 
    Paper, 
    List, 
    ListItem, 
    ListItemText,
    Divider,
    Button // Make sure Button is imported
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp'; // Import a download icon

// --- Dummy Data (EXPORTED for use in parent component) ---
export const mockAllEmployeeData = {
    // 1. Personal & Contact Details
    personal: {
        firstName: "Ankit",
        lastName: "Sharma",
        dob: "1990-08-15",
        gender: "Male",
        maritalStatus: "Married",
        primaryPhone: "+91 98765 43210",
        personalEmail: "ankit.sharma@example.com",
        addressLine1: "123 Green Towers",
        city: "Bangalore",
        pincode: "560001"
    },
    // 2. Employment Details
    employment: {
        employeeId: "EMP-0457",
        designation: "Senior Software Engineer",
        department: "Technology",
        joiningDate: "2020-03-01",
        reportingManager: "Rajesh Kumar",
        employmentType: "Full-Time",
        location: "Bengaluru, India"
    },
    // 3. Compensation & Pay Details
    payroll: {
        salaryType: "Annual",
        grossSalary: 1800000,
        currency: "INR",
        payFrequency: "Monthly",
        isVariablePayApplicable: true,
        variablePayPercentage: 15
    },
    // 4. Banking & Statutory
    bankingStatutory: {
        bankName: "HDFC Bank",
        accountNumber: "50100123456789",
        ifscCode: "HDFC0001234",
        epfNumber: "KA/12345/789",
        uan: "102568459632",
        pan: "ABCDE1234F",
        aadhar: "4567 8901 2345"
    },
    // 5. Policies
    policies: {
        leavePolicy: "probationary",
        shiftType: "a_shift",
        attendanceSource: "online",
        holidayPolicy: "general"
    },
    // 6. Documents (simplified)
    documents: [
        { type: "PAN Card", name: "pan_card_final.pdf" },
        { type: "Graduation Certificate", name: "btech_degree.pdf" },
    ],
    // 7. Exit / Rehire Status
    status: {
        currentStatus: "Active", // For this mock, assume Active
        resignationDate: "N/A",
        rehireDetails: "N/A"
    }
};

const ModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 900,
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    overflowY: 'auto',
};

const SectionPaper = ({ children, title }) => (
    <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {children}
    </Paper>
);

const ViewAllDetailsModal = ({ open, handleClose, employeeData }) => {
    
    const data = employeeData || mockAllEmployeeData;

    // Handler for the new download button
    const handleDownload = () => {
        alert('Download initiated! (This is a placeholder action)');
        // In a real application, you would implement actual download logic here.
        // This might involve:
        // 1. Formatting 'data' into a JSON, CSV, or PDF.
        // 2. Making an API call to a backend service that generates the file.
        // 3. Triggering a browser download of the generated file.
    };

    // Helper function to render a key-value pair
    const DetailItem = ({ label, value }) => (
        <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{value || 'N/A'}</Typography>
        </Grid>
    );

    return (
        <Modal open={open} onClose={handleClose} aria-labelledby="view-all-employee-details">
            <Box sx={ModalStyle}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 2 
                }}>
                    <Typography id="view-all-employee-details" variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                        Full Profile: {data.personal?.firstName || ''} {data.personal?.lastName || ''}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}> {/* Use a Box to group buttons */}
                        {/* NEW: Download Button */}
                        <Button
                            variant="outlined" // Using outlined for a subtle look, or 'contained' if preferred
                            color="primary"
                            startIcon={<GetAppIcon />}
                            onClick={handleDownload}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                        >
                            Download
                        </Button>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
                
                <Divider sx={{ mb: 3 }} />

                {/* --- 1. Personal & Contact Details --- */}
                <SectionPaper title="1. Personal & Contact Details">
                    <Grid container spacing={2}>
                        <DetailItem label="Name" value={`${data.personal?.firstName || ''} ${data.personal?.lastName || ''}`} />
                        <DetailItem label="Date of Birth" value={data.personal?.dob} />
                        <DetailItem label="Gender" value={data.personal?.gender} />
                        <DetailItem label="Marital Status" value={data.personal?.maritalStatus} />
                        <DetailItem label="Primary Phone" value={data.personal?.primaryPhone} />
                        <DetailItem label="Personal Email" value={data.personal?.personalEmail} />
                        <DetailItem label="Address" value={`${data.personal?.addressLine1 || ''}, ${data.personal?.city || ''} - ${data.personal?.pincode || ''}`} />
                    </Grid>
                </SectionPaper>

                {/* --- 2. Employment Details --- */}
                <SectionPaper title="2. Employment Details">
                    <Grid container spacing={2}>
                        <DetailItem label="Employee ID" value={data.employment?.employeeId} />
                        <DetailItem label="Designation" value={data.employment?.designation} />
                        <DetailItem label="Department" value={data.employment?.department} />
                        <DetailItem label="Joining Date" value={data.employment?.joiningDate} />
                        <DetailItem label="Manager" value={data.employment?.reportingManager} />
                        <DetailItem label="Employment Type" value={data.employment?.employmentType} />
                        <DetailItem label="Location" value={data.employment?.location} />
                    </Grid>
                </SectionPaper>

                {/* --- 3. Compensation & Pay --- */}
                <SectionPaper title="3. Compensation & Pay">
                    <Grid container spacing={2}>
                        <DetailItem label="Salary Type" value={data.payroll?.salaryType} />
                        <DetailItem 
                            label="Gross Salary" 
                            value={`${data.payroll?.currency || ''} ${data.payroll?.grossSalary ? data.payroll.grossSalary.toLocaleString() : 'N/A'}`} 
                        />
                        <DetailItem label="Pay Frequency" value={data.payroll?.payFrequency} />
                        <DetailItem label="Variable Pay Applicable" value={data.payroll?.isVariablePayApplicable ? 'Yes' : 'No'} />
                        {data.payroll?.isVariablePayApplicable && (
                            <DetailItem label="Variable Pay Percentage" value={`${data.payroll.variablePayPercentage}%`} />
                        )}
                    </Grid>
                </SectionPaper>
                
                {/* --- 4. Banking & Statutory --- */}
                <SectionPaper title="4. Banking & Statutory">
                    <Grid container spacing={2}>
                        <DetailItem label="Bank Name" value={data.bankingStatutory?.bankName} />
                        <DetailItem label="Account No." value={data.bankingStatutory?.accountNumber} />
                        <DetailItem label="IFSC Code" value={data.bankingStatutory?.ifscCode} />
                        <DetailItem label="EPF Number" value={data.bankingStatutory?.epfNumber} />
                        <DetailItem label="UAN" value={data.bankingStatutory?.uan} />
                        <DetailItem label="PAN" value={data.bankingStatutory?.pan} />
                        <DetailItem label="Aadhar" value={data.bankingStatutory?.aadhar} />
                    </Grid>
                </SectionPaper>

                {/* --- 5. Assign Policies --- */}
                <SectionPaper title="5. Assigned Policies">
                    <Grid container spacing={2}>
                        <DetailItem label="Leave Policy" value={data.policies?.leavePolicy?.toUpperCase()} />
                        <DetailItem label="Shift Type" value={data.policies?.shiftType?.toUpperCase()} />
                        <DetailItem label="Attendance Source" value={data.policies?.attendanceSource?.toUpperCase()} />
                        <DetailItem label="Holiday Policy" value={data.policies?.holidayPolicy?.toUpperCase()} />
                    </Grid>
                </SectionPaper>

                {/* --- 6. Documents & Other --- */}
                <SectionPaper title="6. Documents Uploaded">
                    <List dense>
                        {data.documents && data.documents.length > 0 ? (
                            data.documents.map((doc, index) => (
                                <ListItem key={index} disableGutters>
                                    <ListItemText primary={`${doc.type}:`} secondary={doc.name} sx={{ m: 0 }} />
                                </ListItem>
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>No documents uploaded.</Typography>
                        )}
                    </List>
                </SectionPaper>

                {/* --- 7. Exit / Rehire Status --- */}
                <SectionPaper title="7. Exit / Rehire Status">
                    <Grid container spacing={2}>
                        <DetailItem label="Current Status" value={data.status?.currentStatus} />
                        <DetailItem label="Resignation Date" value={data.status?.resignationDate} />
                        <DetailItem label="Rehire Details" value={data.status?.rehireDetails} />
                    </Grid>
                </SectionPaper>

            </Box>
        </Modal>
    );
};

export default ViewAllDetailsModal;